import React, { useCallback } from "react";
import Slider from "rc-slider";
import "rc-slider/assets/index.css";
import { getPluralEnding } from "@memento/library/text";
import { clampRange } from "@memento/library/numbers";
import { secondsToTime } from "src/libs/cardUtils";
import constants from "src/constants";
import { Alert } from "@memento/ui/Alert";
import {
  percentToDecimal,
  decimalToPercent,
  fixedNumber,
} from "src/utils/numberConversion";
import { TrimRange, trimRangeToDurationRange } from "src/types/clip-editor";
import { cn } from "src/utils/cn";

const {
  clipEditor: {
    trim: {
      MINIMUM_TRIM_LENGTH_SECONDS,
      VIDEO_DURATION_MIN_PRECISION,
      INPUT_RANGE_NUMBER_OF_DECIMALS,
    },
  },
} = constants;

type ClippedScrubberProps = {
  disabled?: boolean;
  /** Where the left handle is locked at. A number between 0 and 100. */
  minTrimPercent: number;
  /** Where the right handle is positioned. A number between 0 and 100. */
  currentTimePercent: number;
  className?: string;
  onChange: (scrubberPercent: number) => void;
  onChangeComplete?: () => void;
};

function ClippedScrubber({
  disabled = false,
  className = "",
  minTrimPercent,
  currentTimePercent,
  onChange,
  onChangeComplete,
}: ClippedScrubberProps) {
  const leftHandleValue = minTrimPercent;
  const rightHandleValue = currentTimePercent;

  const handleChange = useCallback(
    (val: number | number[]) => {
      if (!Array.isArray(val)) {
        return;
      }

      const oldLeftHandleValue = leftHandleValue;
      const oldRightHandleValue = rightHandleValue;

      const newLeftHandleValue = val[0];
      const newRightHandleValue = val[1];

      if (
        newLeftHandleValue === undefined ||
        newRightHandleValue === undefined
      ) {
        return;
      }

      /*
       * What's happening here:
       *
       * If you try to click on the slider track, rc-slider will try to move the
       * left handle if you click in the left 50% of the slider (or something
       * like that) and it won't try to move the right handle at all. But the
       * left handle never moves. So if we always used the right handle value,
       * it won't change if rc-slider tries to move the left handle.
       *
       * So we'll sometimes use the left handle value as the value for the right
       * handle. That way, no matter which handle rc-slider tries to move, we'll
       * always and only move the right handle to where the user clicks.
       *
       * However, rc-slider sometimes moves one of the sliders by a tiny amount;
       * probably some JS floating point math thing.
       *
       * What we end up doing is using the value from the handle that moved the
       * most. And then only use the left handle value if it moved more than a
       * certain amount.
       */

      const scrubberPercent = (() => {
        // Only use the left handle value if it moved more than this amount. If
        // it's moved less than this, then it's likely rc-slider making some JS
        // floating point math error.
        const MIN_LEFT_MOVEMENT = 1;

        if (
          newRightHandleValue === oldRightHandleValue &&
          newLeftHandleValue === oldLeftHandleValue
        ) {
          return newRightHandleValue;
        }

        const leftChange = Math.abs(newLeftHandleValue - oldLeftHandleValue);
        const rightChange = Math.abs(newRightHandleValue - oldRightHandleValue);

        if (leftChange > rightChange && leftChange > MIN_LEFT_MOVEMENT) {
          return newLeftHandleValue;
        } else {
          return newRightHandleValue;
        }
      })();

      onChange(scrubberPercent);
    },
    [onChange, leftHandleValue, rightHandleValue]
  );

  return (
    <Slider
      disabled={disabled}
      range
      allowCross={false}
      value={[leftHandleValue, rightHandleValue]}
      min={0}
      max={100}
      onChange={handleChange}
      onChangeComplete={onChangeComplete}
      step={VIDEO_DURATION_MIN_PRECISION}
      className={className}
      handleRender={(node, data) => {
        // Hide the "min" (left) trimmer handle
        if (data.index === 0) {
          return <></>;
        }

        return (
          <div
            {...node.props}
            className={cn(node.props.className, "-bottom-2.5")}
          ></div>
        );
      }}
    />
  );
}

interface ClipSlidersProps {
  disabled?: boolean;
  currentTime: number;
  trimTimes: TrimRange;
  duration: number;
  onScrubberChange: (num: number) => void; // num is the seconds of playback
  onTrimSliderChange: ({ min, max }: { min: number; max: number }) => void;
  onChangeComplete?: () => void;
  variant?: "vertical" | "horizontal";
}

export const ClipSliders = ({
  disabled = false,
  currentTime,
  trimTimes,
  duration,
  onScrubberChange,
  onTrimSliderChange,
  onChangeComplete,
  variant = "horizontal",
}: ClipSlidersProps) => {
  const currentTimePercent = decimalToPercent(currentTime, duration);
  const minTrimPercent = decimalToPercent(trimTimes.min, duration);
  const maxTrimPercent = decimalToPercent(trimTimes.max, duration);

  const handleScrubber = useCallback(
    (scrubberPercent: number) => {
      const scrubberPercentClamped = clampRange(scrubberPercent, {
        min: minTrimPercent,
        max: maxTrimPercent,
      });

      onScrubberChange(
        fixedNumber(
          percentToDecimal(scrubberPercentClamped, duration),
          INPUT_RANGE_NUMBER_OF_DECIMALS
        )
      );
    },
    [onScrubberChange, duration, minTrimPercent, maxTrimPercent]
  );

  const handleTrim = useCallback(
    (val: number | number[]) => {
      if (!Array.isArray(val)) {
        return;
      }

      const [minPercent, maxPercent] = val;

      if (minPercent === undefined || maxPercent === undefined) {
        return;
      }

      const minTime = percentToDecimal(minPercent, duration);
      const maxTime = percentToDecimal(maxPercent, duration);

      const trimRange = {
        min: fixedNumber(minTime, INPUT_RANGE_NUMBER_OF_DECIMALS),
        max: fixedNumber(maxTime, INPUT_RANGE_NUMBER_OF_DECIMALS),
      };

      onTrimSliderChange(trimRange);

      const durationRange = trimRangeToDurationRange(trimRange, duration);

      // If the start scrubber has changed, seek to the new start of the clip
      if (
        fixedNumber(
          durationRange.start * 100,
          INPUT_RANGE_NUMBER_OF_DECIMALS
        ) !== fixedNumber(minTrimPercent, INPUT_RANGE_NUMBER_OF_DECIMALS)
      ) {
        onScrubberChange(
          fixedNumber(
            percentToDecimal(minPercent, duration),
            INPUT_RANGE_NUMBER_OF_DECIMALS
          )
        );
        // If the end scrubber has changed, seek to the new end of the clip
      } else if (
        fixedNumber(durationRange.end * 100, INPUT_RANGE_NUMBER_OF_DECIMALS) !==
        fixedNumber(maxTrimPercent, INPUT_RANGE_NUMBER_OF_DECIMALS)
      ) {
        onScrubberChange(
          fixedNumber(
            // Subtract 1 frame (assuming 30 fps) to prevent the end scrubber from going past the end of the clip
            percentToDecimal(maxPercent, duration) - 1 / 30,
            INPUT_RANGE_NUMBER_OF_DECIMALS
          )
        );
      }
    },
    [
      onScrubberChange,
      onTrimSliderChange,
      duration,
      minTrimPercent,
      maxTrimPercent,
    ]
  );

  return (
    <div
      className={cn(variant === "vertical" ? "flex w-full flex-col gap-6" : "")}
    >
      <ClippedScrubber
        disabled={disabled}
        className={
          variant === "vertical" ? "scrubber-slider-active" : "scrubber-slider"
        }
        minTrimPercent={minTrimPercent}
        currentTimePercent={currentTimePercent}
        onChange={handleScrubber}
        onChangeComplete={onChangeComplete}
      />

      {variant === "horizontal" ? <div className="h-6" /> : null}

      <Slider
        disabled={disabled}
        allowCross={false}
        marks={{
          0: {
            style: { top: "0.5rem" },
            label: "0s",
          },
          100: {
            style: { top: "0.5rem" },
            label: `${secondsToTime(duration)}`,
          },
        }}
        className="trim-slider"
        value={[minTrimPercent, maxTrimPercent]}
        onChange={handleTrim}
        onChangeComplete={onChangeComplete}
        range
        step={VIDEO_DURATION_MIN_PRECISION}
      />

      {duration <= MINIMUM_TRIM_LENGTH_SECONDS ? (
        <Alert variant="warning" className="mt-9">
          Video clips must be more than {MINIMUM_TRIM_LENGTH_SECONDS} second
          {getPluralEnding(MINIMUM_TRIM_LENGTH_SECONDS)} long to be trimmed.
        </Alert>
      ) : null}
    </div>
  );
};
